import { isEmailValidByDNSLookup } from "../common/isEmailValidByDNSLookup.ts";
import validator from "validator";
import { ENV } from "../../constants/common.ts";
import { isValidDate, toLocalCalendarDate } from "./localCalendarDate.ts";
import { hasCurpFormat } from "./curp";

export const anyValidator = (_: any) => Promise.resolve();

export const validateAlpha = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const alphaRegex = /^[A-Za-zÀ-ÿ\s]+$/;
  if (!alphaRegex.test(value))
    return Promise.reject(new Error("Favor de proporcionar solo letras"));

  return Promise.resolve();
};

export const validateAlphaNumeric = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const alphaNumericRegex = /^[a-zA-Z0-9À-ÿ ]+$/;
  if (!alphaNumericRegex.test(value))
    return Promise.reject(
      new Error("Favor de proporcionar solo letras y números"),
    );

  return Promise.resolve();
};

export const validateClabe = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const clabeRegex = /^\d{18}$/;
  if (!clabeRegex.test(value))
    return Promise.reject(new Error("Favor de proporcionar una CLABE válida"));

  // Control-digit check (weights 3,7,1 over the first 17 digits) — mirrors
  // the backend rule so a typo'd CLABE fails on blur, not after a round trip.
  const weights = [3, 7, 1];
  const total = value
    .slice(0, 17)
    .split("")
    .reduce((sum, digit, i) => sum + Number(digit) * weights[i % 3], 0);
  if ((10 - (total % 10)) % 10 !== Number(value[17]))
    return Promise.reject(
      new Error("La CLABE no es válida, revisa que esté bien copiada"),
    );

  return Promise.resolve();
};

// MB-483: a landlord may be paid to a bank account or a card instead of a CLABE.
// Each type has its own rule, so a card no longer has to be zero-padded to 18
// digits to clear validateClabe (folio 311330). Messages mirror the backend's
// form_validation.py so a field reads the same wherever it is rejected.
export const validateAccountNumber = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  // Mexican account numbers are 10-11 digits and carry no published control
  // digit (unlike CLABE), so length is the only rule we can enforce.
  if (!/^\d{10,11}$/.test(value))
    return Promise.reject(
      new Error("Favor de proporcionar un número de cuenta de 10 u 11 dígitos"),
    );

  return Promise.resolve();
};

export const validateCardNumber = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  if (!/^\d{16}$/.test(value))
    return Promise.reject(
      new Error("Favor de proporcionar un número de tarjeta de 16 dígitos"),
    );

  // Luhn check — mirrors the backend so a typo'd card fails on blur, not after
  // a round trip (same rationale as the CLABE control digit above).
  const total = value
    .split("")
    .reverse()
    .reduce((sum, digit, i) => {
      let n = Number(digit);
      if (i % 2 === 1) {
        n *= 2;
        if (n > 9) n -= 9;
      }
      return sum + n;
    }, 0);
  if (total % 10 !== 0)
    return Promise.reject(
      new Error("El número de tarjeta no es válido, revisa que esté bien copiado"),
    );

  return Promise.resolve();
};

export const validateUrl = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const isValidURL = validator.isURL(value);
  if (!isValidURL)
    return Promise.reject(new Error("Favor de proporcionar una URL válida"));

  return Promise.resolve();
};

export const validateNumeric = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const numericRegex = /^\d+$/;
  const toNumber = Number(value);

  if (isNaN(toNumber))
    return Promise.reject(new Error("Favor de proporcionar solo números"));
  if (toNumber <= 0)
    return Promise.reject(new Error("Favor de proporcionar números positivos"));

  if (!numericRegex.test(value))
    return Promise.reject(
      new Error("Favor de proporcionar solo números enteros"),
    );

  return Promise.resolve();
};

// Like validateNumeric but accepts up to 2 decimals (positive amounts, e.g. deposit_amount).
export const validateDecimal = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const decimalRegex = /^\d+(\.\d{0,2})?$/;
  const toNumber = Number(value);

  if (isNaN(toNumber))
    return Promise.reject(new Error("Favor de proporcionar solo números"));
  if (toNumber <= 0)
    return Promise.reject(new Error("Favor de proporcionar números positivos"));

  if (!decimalRegex.test(value))
    return Promise.reject(
      new Error("Favor de proporcionar un monto válido (máximo 2 decimales)"),
    );

  return Promise.resolve();
};

export const validateNumericWithZero = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const numericRegex = /^\d+$/;
  const toNumber = Number(value);

  if (isNaN(toNumber))
    return Promise.reject(new Error("Favor de proporcionar solo números"));
  if (toNumber < 0)
    return Promise.reject(new Error("Favor de proporcionar números positivos"));

  if (!numericRegex.test(value))
    return Promise.reject(
      new Error("Favor de proporcionar solo números enteros"),
    );

  return Promise.resolve();
};

// Like validateNumericWithZero but accepts up to 2 decimals (rent / maintenance amounts).
export const validateDecimalWithZero = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const decimalRegex = /^\d+(\.\d{0,2})?$/;
  const toNumber = Number(value);

  if (isNaN(toNumber))
    return Promise.reject(new Error("Favor de proporcionar solo números"));
  if (toNumber < 0)
    return Promise.reject(new Error("Favor de proporcionar números positivos"));

  if (!decimalRegex.test(value))
    return Promise.reject(
      new Error("Favor de proporcionar un monto válido (máximo 2 decimales)"),
    );

  return Promise.resolve();
};

export const validateCURP = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  if (!hasCurpFormat(value))
    return Promise.reject(new Error("Favor de proporcionar un CURP válido"));

  return Promise.resolve();
};

export const validateRFC = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const rfcRegex =
    /^([A-ZÑ\x26]{4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{2}([A]|[0-9]){1})?$/;
  if (value && !rfcRegex.test(value))
    return Promise.reject(new Error("Favor de proporcionar un RFC válido"));

  return Promise.resolve();
};

export const validateCompanyRFC = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const rfcRegex =
    /^([A-ZÑ\x26]{3}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{2}([A]|[0-9]){1})?$/;
  if (value && !rfcRegex.test(value))
    return Promise.reject(new Error("Favor de proporcionar un RFC válido"));

  return Promise.resolve();
};

export const validateAnyRFC = async (_: any, value: string) => {
  if (!value) return Promise.resolve();

  try {
    await validateRFC(_, value);

    return Promise.resolve();
  } catch (_: any) {
    try {
      await validateCompanyRFC(_, value);

      return Promise.resolve();
    } catch (_error: any) {
      return Promise.reject(new Error(`Favor de proporcionar un RFC válido`));
    }
  }
};

export const validatePhoneNumber = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const phoneRegExp = /^\d{10}$/;
  if (!value || !phoneRegExp.test(value))
    return Promise.reject(
      new Error("Favor de ingresar un número de 10 dígitos"),
    );

  return Promise.resolve();
};

export const validateBirthDate = (_: any, value: any) => {
  if (!value) return Promise.resolve();

  const selectedDate = toLocalCalendarDate(value);
  // Unparseable values used to slip through (NaN passes both range checks)
  // and reach the backend; reject them explicitly instead.
  if (!isValidDate(selectedDate))
    return Promise.reject(new Error("Favor de proporcionar una fecha válida"));

  if (selectedDate.getFullYear() < 1900)
    return Promise.reject(
      new Error("La fecha de nacimiento debe ser posterior a 1899"),
    );

  // Date-accurate adulthood check: compare against the actual 18th birthday,
  // not a year subtraction (which passed people who turn 18 later this year).
  const eighteenthBirthday = new Date(
    selectedDate.getFullYear() + 18,
    selectedDate.getMonth(),
    selectedDate.getDate(),
  );
  if (eighteenthBirthday > new Date())
    return Promise.reject(new Error("La persona debe de ser mayor de 18 años"));

  return Promise.resolve();
};

export const validateZipCode = (_: any, value: any) => {
  if (!value) return Promise.resolve();

  const zipCodeRegExp = /^\d{5}$/;
  if (!value || !zipCodeRegExp.test(value))
    return Promise.reject(
      new Error("Favor de ingresar un código postal de 5 dígitos"),
    );

  return Promise.resolve();
};

export const validateNameAndPhoneNumber = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const nameAndPhoneNumberRegex = /^[A-Za-zÀ-ÿ\s]+ \d{10}$/;
  if (!nameAndPhoneNumberRegex.test(value))
    return Promise.reject(new Error("Favor de seguir el formato"));

  return Promise.resolve();
};

export const validateNameAndNumber = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const nameAndNumberRegex = /^[A-Za-zÀ-ÿ.\s]+ \d+$/;
  if (!nameAndNumberRegex.test(value))
    return Promise.reject(new Error("Favor de seguir el formato"));

  return Promise.resolve();
};

export const validateNumberAndName = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const numberAndNameRegex = /^\d+ [A-Za-zÀ-ÿ.\s]+$/;
  if (!numberAndNameRegex.test(value))
    return Promise.reject(new Error("Favor de seguir el formato"));

  return Promise.resolve();
};

export const validateDateGreaterOrEqualThanToday = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const selectedDate = toLocalCalendarDate(value);
  if (!isValidDate(selectedDate))
    return Promise.reject(new Error("Favor de proporcionar una fecha válida"));

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (selectedDate < today)
    return Promise.reject(
      new Error("La fecha debe ser mayor o igual a la actual"),
    );

  return Promise.resolve();
};

export const validateDateLessOrEqualThanToday = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const selectedDate = toLocalCalendarDate(value);
  if (!isValidDate(selectedDate))
    return Promise.reject(new Error("Favor de proporcionar una fecha válida"));

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (selectedDate > today)
    return Promise.reject(
      new Error("La fecha debe ser menor o igual a la actual"),
    );

  return Promise.resolve();
};

export const validateDayOfTheMonth = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  // Integer-only, mirroring the backend rule — Number() also accepted
  // decimals ("5.5") that the server rejects.
  if (!/^\d+$/.test(value) || Number(value) < 1 || Number(value) > 31)
    return Promise.reject(new Error("Favor de proporcionar un día válido"));

  return Promise.resolve();
};

export const validateStreet = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const streetRegex = /^[a-zA-Z0-9À-ÿ .]+$/;
  if (!streetRegex.test(value))
    return Promise.reject(new Error("Favor de proporcionar una calle válida"));

  return Promise.resolve();
};

export const validateCurrency = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const currencyRegex = /^[0-9]+$/;
  if (!currencyRegex.test(value))
    return Promise.reject(
      new Error("Favor de proporcionar una cantidad válida"),
    );

  return Promise.resolve();
};

export const validatePropertyNumber = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  const propertyNumberRegex = /^[a-zA-Z0-9]+(?:\s*[-]?\s*[a-zA-Z0-9]+)?$/;

  if (!propertyNumberRegex.test(value))
    return Promise.reject(
      new Error("Favor de proporcionar un numero de vivienda válido"),
    );

  return Promise.resolve();
};

// Local-only email check (no network). The draft auto-save path runs on every
// debounced form change and must never depend on the DNS lookup service.
export const validateEmailLocal = (_: any, value: string) => {
  if (!value) return Promise.resolve();

  if (!validator.isEmail(value)) {
    return Promise.reject(new Error("Favor de proporcionar un email válido"));
  }

  return Promise.resolve();
};

export const validateEmail = async (_: any, value: string) => {
  if (!value) return Promise.resolve();

  if (!validator.isEmail(value)) {
    return Promise.reject(new Error("Favor de proporcionar un email válido"));
  }

  if (ENV !== "development") {
    const lookupResult = await isEmailValidByDNSLookup(value);
    if (lookupResult === "invalid") {
      // Distinct message: the format is fine but the domain check failed.
      // Reusing the format-error message here made this case undiagnosable
      // for users whose email looked (and was) correctly written.
      return Promise.reject(
        new Error(
          "No pudimos verificar el dominio de este correo. Revisa que esté bien escrito (ej. gmail.com, hotmail.com)",
        ),
      );
    }
    // "unavailable" falls through: the format already passed and the DNS
    // service couldn't answer, so we don't block the user (fail-open).
  }

  return Promise.resolve();
};
